import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader2, Building2, Wrench, ArrowLeft, LogOut, Shield } from "lucide-react";
import { EntityType } from "../../types";
import { formatDate } from "../../lib/formatDate";

const API_URL = process.env.REACT_APP_API_URL || "";
const ADMIN_AUTH_KEY = "unikort_admin_authenticated";

interface EntitiesResponse {
  buildings: Array<{ id: string; name: string; address: string }>;
  serviceProviders: Array<{
    id: string;
    name: string;
    services: string[];
    serviceNames: string[];
    city?: string;
    registryCode?: string | null;
    registryCodeVerifiedAt?: string | null;
  }>;
}

export function AdminImpersonatePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { loginAsEntity } = useAuth();

  const [activeTab, setActiveTab] = useState<EntityType>("building");
  const [entities, setEntities] = useState<EntitiesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBuildingId, setSelectedBuildingId] = useState("");
  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
        if (!me.data?.isAdmin) {
          sessionStorage.removeItem(ADMIN_AUTH_KEY);
          navigate("/admin", { replace: true });
          return;
        }
        sessionStorage.setItem(ADMIN_AUTH_KEY, "true");

        const res = await axios.get<{ success: boolean; data: EntitiesResponse }>(
          `${API_URL}/api/admin/entities`,
          { withCredentials: true }
        );
        if (res.data.success) setEntities(res.data.data);
      } catch {
        sessionStorage.removeItem(ADMIN_AUTH_KEY);
        navigate("/admin", { replace: true });
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [navigate, t]);

  /**
   * The operator's grant of the seller mark (ticket 25, D5): only a confirmed
   * company may issue invoices in its own name.
   */
  const selectedProvider = entities?.serviceProviders.find((p) => p.id === selectedProviderId) ?? null;
  const [verifying, setVerifying] = useState(false);
  const handleVerification = async (verified: boolean) => {
    if (!selectedProvider) return;
    setVerifying(true);
    setError("");
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/service-providers/${encodeURIComponent(selectedProvider.id)}/registry-verification`,
        { verified },
        { withCredentials: true }
      );
      const at: string | null = res.data?.data?.registryCodeVerifiedAt ?? null;
      setEntities((prev) =>
        prev
          ? {
              ...prev,
              serviceProviders: prev.serviceProviders.map((p) =>
                p.id === selectedProvider.id ? { ...p, registryCodeVerifiedAt: at } : p
              ),
            }
          : prev
      );
    } catch {
      setError(t("admin.verificationFailed"));
    } finally {
      setVerifying(false);
    }
  };

  const handleImpersonate = async () => {
    const id = activeTab === "building" ? selectedBuildingId : selectedProviderId;
    if (!id) return;
    setIsSubmitting(true);
    setError("");
    try {
      await loginAsEntity(activeTab, id);
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.failedToLogin"));
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side */}
      <div className="flex-1 bg-card px-4 sm:px-6 py-5 flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-[480px]">
            <div className="flex items-center justify-between mb-6">
              <Button variant="ghost" size="sm" onClick={() => navigate("/admin/dashboard")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("admin.backToDashboard")}
              </Button>
            </div>

            <h1 className="text-2xl font-semibold mb-6">{t("admin.selectEntity")}</h1>

            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-4 rounded-xl border border-destructive/20 mb-4">
                {error}
              </div>
            )}

            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as EntityType)}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="building">
                  <Building2 className="w-4 h-4 mr-2" /> {t("admin.building")}
                </TabsTrigger>
                <TabsTrigger value="service_provider">
                  <Wrench className="w-4 h-4 mr-2" /> {t("admin.serviceProvider")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="building" className="mt-4">
                <Select value={selectedBuildingId} onValueChange={setSelectedBuildingId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("admin.chooseBuilding")} />
                  </SelectTrigger>
                  <SelectContent>
                    {entities?.buildings.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TabsContent>

              <TabsContent value="service_provider" className="mt-4">
                <Select value={selectedProviderId} onValueChange={setSelectedProviderId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("admin.chooseProvider")} />
                  </SelectTrigger>
                  <SelectContent>
                    {entities?.serviceProviders.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        <span className="font-medium">{p.name}</span>
                        {p.serviceNames.length > 0 && (
                          <span className="ml-2 opacity-70">
                            ({p.serviceNames.join(", ")})
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedProvider && (
                  <div className="mt-4 rounded-xl border border-border p-4 text-sm" data-testid="seller-verification">
                    <p className="font-medium text-foreground">{t("admin.sellerVerification")}</p>
                    {selectedProvider.registryCode ? (
                      <>
                        <p className="mt-1 text-muted-foreground">
                          {t("admin.registryCodeLabel", { code: selectedProvider.registryCode })}
                          {" · "}
                          {selectedProvider.registryCodeVerifiedAt
                            ? t("admin.sellerVerifiedSince", {
                                date: formatDate(selectedProvider.registryCodeVerifiedAt, i18n.language),
                              })
                            : t("admin.sellerNotVerified")}
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-3"
                          disabled={verifying}
                          onClick={() => handleVerification(!selectedProvider.registryCodeVerifiedAt)}
                        >
                          {verifying && <Loader2 className="w-4 h-4 animate-spin" />}
                          {selectedProvider.registryCodeVerifiedAt ? t("admin.unverifySeller") : t("admin.verifySeller")}
                        </Button>
                      </>
                    ) : (
                      <p className="mt-1 text-muted-foreground">{t("admin.noRegistryCode")}</p>
                    )}
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <Button
              className="w-full mt-6"
              size="lg"
              disabled={isSubmitting}
              onClick={handleImpersonate}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <LogOut className="w-4 h-4 mr-2" />
              )}
              {t("admin.loginAs", {
                entity: activeTab === "building" ? t("admin.building") : t("admin.serviceProvider"),
              })}
            </Button>
          </div>
        </div>
      </div>

      {/* Right side: brand panel */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-amber-500 to-orange-600 items-center justify-center">
        <div className="text-center text-white">
          <Shield className="h-24 w-24 mx-auto mb-6 opacity-80" />
          <h2 className="text-3xl font-bold mb-2">{t("admin.adminPortal")}</h2>
          <p className="text-white/80 max-w-xs">
            {t("admin.adminPortalDesc")}
          </p>
        </div>
      </div>
    </div>
  );
}
